import { useCallback, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from '../components/Toast';

const FUNCTIONS_BASE = 'https://us-central1-sistema-bks.cloudfunctions.net';

/**
 * Full-subscription cancel/reactivate flow — /billing only (not /addon).
 * Always schedules cancellation for period end (cancel_at_period_end: true),
 * never immediate. Mirrors the wait-for-webhook pattern from
 * useUpgradeToPro/useDowngradeToStarter: the endpoint only flips the Stripe
 * flag, customer.subscription.updated (stripeWebhook.js) is the single
 * writer for organizations/{orgId} / clientes/{uid}.
 *
 * @param {{mode: string, orgId?: string}} context - from useOrganization()
 */
// onSynced (opcional): si se pasa, se llama en vez de window.location.reload()
// cuando el webhook confirma el cambio (mismo patrón que useUpgradeToPro).
export default function useCancelSubscription(context, { onSynced } = {}) {
  const [processing, setProcessing] = useState(false);
  const { t } = useTranslation('billing');
  const auth = getAuth();
  const db = getFirestore();

  const orgPayload = useCallback(() => (
    context.mode === 'organization' && context.orgId ? { organizationId: context.orgId } : {}
  ), [context.mode, context.orgId]);

  const waitForCancelSync = useCallback((targetCancelAtPeriodEnd) => {
    return new Promise((resolve) => {
      const user = auth.currentUser;
      const docRef = context.mode === 'organization' && context.orgId
        ? doc(db, 'organizations', context.orgId)
        : doc(db, 'clientes', user.uid);

      let settled = false;

      const finish = (synced) => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        toast[synced ? 'success' : 'info'](t(synced ? 'subscriptionCancelModal.synced' : 'subscriptionCancelModal.syncDelayed'));
        if (onSynced) onSynced(); else window.location.reload();
        resolve();
      };

      const unsubscribe = onSnapshot(docRef, (snap) => {
        const data = snap.data();
        const cancelAtPeriodEnd = !!data?.billing?.cancelAtPeriodEnd;
        if (cancelAtPeriodEnd === targetCancelAtPeriodEnd) finish(true);
      }, () => finish(false));

      const timeoutId = setTimeout(() => finish(false), 7000);
    });
  }, [auth, context.mode, context.orgId, db, t, onSynced]);

  const cancelarSuscripcion = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(t('loginRequired'));
      return;
    }

    setProcessing(true);
    try {
      const idToken = await user.getIdToken();
      await axios.post(
        `${FUNCTIONS_BASE}/cancelSubscription`,
        orgPayload(),
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      await waitForCancelSync(true);
    } catch (error) {
      console.error('❌ Error al cancelar suscripción:', error);
      toast.error(error.response?.data?.error || t('subscriptionCancelModal.error'));
    } finally {
      setProcessing(false);
    }
  }, [auth, orgPayload, waitForCancelSync, t]);

  const mantenerSuscripcion = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(t('loginRequired'));
      return;
    }

    setProcessing(true);
    try {
      const idToken = await user.getIdToken();
      const payload = { ...orgPayload(), undo: true };
      await axios.post(
        `${FUNCTIONS_BASE}/cancelSubscription`,
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      await waitForCancelSync(false);
    } catch (error) {
      console.error('❌ Error al reactivar suscripción:', error);
      toast.error(error.response?.data?.error || t('subscriptionCancelModal.error'));
    } finally {
      setProcessing(false);
    }
  }, [auth, orgPayload, waitForCancelSync, t]);

  return { processing, cancelarSuscripcion, mantenerSuscripcion };
}
